/**
 * Sector Rotation Radar Widget — RS + Trend + Flow scoring
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tooltip, IconButton, alpha, Skeleton,
} from '@mui/material';
import {
  InfoOutlined, TrendingUp, TrendingDown, Radar,
  LocalFireDepartment, AccountBalance, ShoppingCart, Factory,
  Construction, HealthAndSafety, Computer, Wifi, Store,
  ElectricBolt, Home,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { portfolioAPI } from '../../services/api';

const SECTOR_ICON_KEYWORDS = [
  [/energy|năng lượng|dầu|khí/i, LocalFireDepartment],
  [/financ|tài chính|ngân hàng|bank/i, AccountBalance],
  [/staple|tiêu dùng(?!.*thiết)/i, ShoppingCart],
  [/industrial|công nghiệp/i, Factory],
  [/material|vật liệu|nguyên liệu/i, Construction],
  [/health|sức khỏe|y tế|dược/i, HealthAndSafety],
  [/tech|công nghệ|thông tin/i, Computer],
  [/comm|viễn thông|truyền thông/i, Wifi],
  [/discret|thiết yếu|bán lẻ/i, Store],
  [/utilit|tiện ích|điện|nước/i, ElectricBolt],
  [/real.?estate|bất động sản|nhà đất/i, Home],
];
const getSectorIcon = name => {
  const s = String(name || '').replace(/_/g, ' ');
  return (SECTOR_ICON_KEYWORDS.find(([re]) => re.test(s)) || [])[1] || Factory;
};

const clrScore = s => s >= 1.5 ? '#10b981' : s >= 0.5 ? '#22c55e' : s >= 0 ? '#f59e0b' : s >= -0.5 ? '#f97316' : '#ef4444';

const SectorRotationRadarWidget = ({ calcDate = null }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectors, setSectors] = useState([]);

  useEffect(() => { fetchData(); }, [calcDate]); // eslint-disable-line

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const r = await portfolioAPI.getSafeInvestSectorRotation();
      const data = r.data;
      if (data.success && data.sectors) {
        setSectors(data.sectors.map((s, i) => ({
          sector_code: s.sector, sector_name: s.sector_name,
          score: s.score, rs_momentum: s.rs_momentum || 0,
          trend_score: s.trend || 0, flow_score: s.flow || 0, rank: i + 1,
        })));
      } else throw new Error('Invalid response');
    } catch (e) { setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const sectorLabel = code => {
    // Use sector_name from API if available, fallback to prettified code
    const found = sectors.find(s => s.sector_code === code);
    const name = found?.sector_name;
    if (name) {
      if (typeof name === 'object' && (name.en || name.vi)) {
        return i18n.language === 'vi' ? (name.vi || name.en) : (name.en || name.vi);
      }
      return name;
    }
    return (code || '').replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', height: '100%', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={26} height={26} sx={{ borderRadius: 1 }} animation="wave" />
          <Skeleton width={120} height={16} animation="wave" />
        </Box>
        <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
          {[0,1,2,3,4,5,6,7,8,9,10].map(i => <Skeleton key={i} height={32} sx={{ mb: 0.3 }} animation="wave" />)}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2, height: '100%' }}>
        <Typography sx={{ fontSize: 12, color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Radar sx={{ fontSize: 14, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
              {t('tradingDecision.sectorRotation.title', 'Luân Chuyển Ngành')}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
              {t('tradingDecision.sectorRotation.subtitle', 'RS + Trend + Flow')}
            </Typography>
          </Box>
        </Box>
        <Tooltip title={t('tradingDecision.sectorRotation.info', 'Score = RS Momentum + Trend + Money Flow. > 0 = dòng tiền vào')}>
          <IconButton size="small" sx={{ width: 22, height: 22 }}><InfoOutlined sx={{ fontSize: 13, color: '#94a3b8' }} /></IconButton>
        </Tooltip>
      </Box>

      {/* Sector List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 0.5 }}>
        {sectors.map((s, idx) => {
          const c = clrScore(s.score);
          const SIcon = getSectorIcon(s.sector_code);
          const isTop3 = idx < 3;

          return (
            <Box key={s.sector_code} sx={{
              display: 'flex', alignItems: 'center', py: 0.55,
              borderBottom: idx < sectors.length - 1 ? '1px solid #f8fafc' : 'none',
            }}>
              {/* Rank */}
              <Typography sx={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mr: 0.6,
                bgcolor: isTop3 ? (idx === 0 ? '#fbbf24' : idx === 1 ? '#a1a1aa' : '#d97706') : alpha('#94a3b8', 0.08),
                color: isTop3 ? '#fff' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800,
              }}>
                {s.rank}
              </Typography>

              {/* Icon */}
              <SIcon sx={{ fontSize: 14, color: '#94a3b8', mr: 0.5, flexShrink: 0 }} />

              {/* Name */}
              <Typography sx={{
                flex: 1, minWidth: 0, fontSize: 12, fontWeight: isTop3 ? 600 : 500,
                color: isTop3 ? '#1e293b' : '#475569',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {sectorLabel(s.sector_code)}
              </Typography>

              {/* RS / T / F tags */}
              <Box sx={{ display: 'flex', gap: 0.25, mr: 0.6, flexShrink: 0 }}>
                {[
                  { l: 'RS', v: s.rs_momentum, bg: '#3b82f6' },
                  { l: 'T', v: s.trend_score, bg: '#8b5cf6' },
                  { l: 'F', v: s.flow_score, bg: '#f59e0b' },
                ].map(tag => (
                  <Tooltip key={tag.l} title={`${tag.l === 'RS' ? t('tradingDecision.sectorRotation.rsMomentum', 'RS Momentum') : tag.l === 'T' ? t('tradingDecision.sectorRotation.trend', 'Trend') : t('tradingDecision.sectorRotation.flow', 'Flow')}: ${tag.v.toFixed(1)}`} arrow>
                    <Typography sx={{
                      fontSize: 9, fontWeight: 700, lineHeight: 1, px: 0.4, py: 0.2,
                      borderRadius: 0.5, bgcolor: alpha(tag.bg, 0.08), color: tag.bg,
                    }}>
                      {tag.v >= 0 ? '+' : ''}{tag.v.toFixed(1)}
                    </Typography>
                  </Tooltip>
                ))}
              </Box>

              {/* Score */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, minWidth: 34, justifyContent: 'flex-end' }}>
                {s.score >= 0 ? <TrendingUp sx={{ fontSize: 10, color: c }} /> : <TrendingDown sx={{ fontSize: 10, color: c }} />}
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: c, fontFamily: 'monospace' }}>
                  {s.score.toFixed(1)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {sectors.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Radar sx={{ fontSize: 24, color: '#cbd5e1', mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{t('tradingDecision.sectorRotation.noData', 'Không có dữ liệu')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SectorRotationRadarWidget;
